import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {NotificationService} from '@services/notification/notification.service';
import {StorageService} from '@services/storage/storage.service';
import {TranslateService} from '@ngx-translate/core';
import {User} from '@models/user/user';
import {BrandingService} from '@services/administration/organization/configuration/branding/branding.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {AuthenticationRedirectService} from '@services/user/authentication-redirect.service';
import firebase from 'firebase/compat/app';
import {ReCaptchaV3Service} from 'ng-recaptcha';
import {Subscription} from 'rxjs';


@Component({
    selector: 'scitis-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  form: UntypedFormGroup;
  submitted = false;

  public loginPending: boolean = false;
  public signInPending: boolean = false;
  public googleSignInPending: boolean = false;
  public showErrors: boolean = false;
  public isPasswordVisible: boolean = false;

  private PAGE_NAME: string = 'LOGIN';

  private readonly resetPasswordDisabledTitle: string;
  private readonly resetPasswordEnabledTitle: string;

  private userPasswordSignInSubscription: Subscription | null = null;

  constructor(private formBuilder: UntypedFormBuilder,
              private router: Router,
              private notificationService: NotificationService,
              private brandingService: BrandingService,
              private storageService: StorageService,
              private translate: TranslateService,
              private firebaseAuthService: FirebaseAuthenticationService,
              private recaptchaV3Service: ReCaptchaV3Service,
              private authenticationRedirectService: AuthenticationRedirectService) {

    this.form = this.formBuilder.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });

    this.resetPasswordDisabledTitle = this.translate.instant('Fill email for which to reset password');
    this.resetPasswordEnabledTitle = this.translate.instant('Reset password');
  }

  ngOnInit(): void {
    this.brandingService.removeOrganizationBranding();
    this.brandingService.closePreviewToast();
    this.brandingService.disableSecondaryColor();
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  public get resetPasswordTitle(): string {
    if (this.shouldDisableResetPasswordButton) {
      return this.resetPasswordDisabledTitle;
    }
    return this.resetPasswordEnabledTitle;
  }

  public get shouldDisableSignInButton(): boolean {
    if (this.submitted) {
      return true;
    }

    return !!this.f.username.errors || !!this.f.password.errors;
  }

  public get shouldDisableResetPasswordButton(): boolean {
    return !!this.f.username.errors;
  }

  onShowPasswordChange() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  onFirebaseSignIn() {
    this.signInPending = true;

    if (this.form.invalid) {
      this.signInPending = false;
      return;
    }

    if (this.userPasswordSignInSubscription) {
      this.userPasswordSignInSubscription.unsubscribe();
    }

    this.userPasswordSignInSubscription = this.recaptchaV3Service.execute('signIn')
    .subscribe({
      next: (token: string) => {
        this.firebaseAuthService.signIn(this.form.value.username, this.form.value.password).then((success: firebase.auth.UserCredential) => {
          this.firebaseAuthService.persistFirebaseDetails(success, token).subscribe({
            next: (persisted) => {
              this.signInPending = false;
              this.handleLoginSuccess(persisted);
            }, error: (failure) => {
              this.signInPending = false;
              this.handleLoginError(failure);
            }
          });
        }, (error) => {
          this.handleLoginError(error);
          this.signInPending = false;
        }).catch((error) => {
          this.handleLoginError(error);
        });
      }, error: (_error) => {
        this.signInPending = false;
        this.handleCaptchaLoginError();
      }
    });
  }

  onGoogleSignIn() {
    this.googleSignInPending = true;

    this.firebaseAuthService.googleSignIn().then((success) => {
      this.firebaseAuthService.persistFirebaseDetails(success, 'GOOGLE').subscribe({
        next: (persisted) => {
          this.googleSignInPending = false;
          this.handleLoginSuccess(persisted);
        }, error: (failure) => {
          this.googleSignInPending = false;
          this.handleLoginError(failure);
        }
      });
    }, (error) => {
      this.handleLoginError(error);
      this.googleSignInPending = false;
    }).catch((error) => {
      this.handleLoginError(error);
      this.googleSignInPending = false;
    });
  }


  public resetPassword() {
    if (!this.form.value.username) {
      const message = this.translate.instant('Type in your email first');
      this.notificationService.warning(message, this.PAGE_NAME);
      return;
    }
    if (this.userPasswordSignInSubscription) {
      this.userPasswordSignInSubscription.unsubscribe();
    }

    this.userPasswordSignInSubscription = this.recaptchaV3Service.execute('resetPassword')
    .subscribe((_token: string) => {
      this.firebaseAuthService.resetPassword(this.form.value.username)
      .then((_success) => {
            const message = this.translate.instant('A password reset link has been sent to your email address');
            this.notificationService.success(message, this.PAGE_NAME, true);
          },
          (error) => {
            const message = this.translate.instant('Error sending the password change link.');
            const firebaseError = this.firebaseAuthService.parseFirebaseResponseErrorCode(error);
            this.notificationService.error(message + firebaseError, error, this.PAGE_NAME, true);
          })
      .catch(error => {
        const message = this.translate.instant('An error occurred while attempting to reset your password');
        const firebaseError = this.firebaseAuthService.parseFirebaseResponseErrorCode(error);
        this.notificationService.error(message + firebaseError, error, this.PAGE_NAME, true);
      });
    });
  }

  ngOnDestroy() {

    if (this.userPasswordSignInSubscription) {
      this.userPasswordSignInSubscription.unsubscribe();
    }
  }

  private handleLoginSuccess(userDetails: User): void {
    this.brandingService.applyOrganizationStyles(userDetails.parentId, true);
    this.loginPending = false;
    const message: string = this.translate.instant('Login success: User: $user ($name).Language: $language ')
    .replace('$user', userDetails.displayName)
    .replace('$name', userDetails.username)
    .replace('$language', userDetails.language);
    this.notificationService.success(message, this.PAGE_NAME, true);
    this.storageService.writeValueToLocalStorage('UILanguage', userDetails.language);
    if (userDetails.agreementAccepted) {
      const externalRedirect = this.authenticationRedirectService.consumeExternalRedirect();
      if (externalRedirect) {
        this.authenticationRedirectService.clearReturnUrl();
        this.authenticationRedirectService.navigateToExternalRedirect(externalRedirect);
        return;
      }
      const returnUrl = this.authenticationRedirectService.consumeReturnUrl();
      if (returnUrl) {
        this.router.navigateByUrl(returnUrl);
      } else {
        this.router.navigate(['dashboard']);
      }
    } else {
      this.authenticationRedirectService.clearExternalRedirect();
      this.authenticationRedirectService.clearReturnUrl();
      this.router.navigate(['agreement']);
    }
  }

  private handleLoginError(error: any): void {
    const message = this.translate.instant('Error logging in.') + ' ';
    const firebaseError = this.firebaseAuthService.parseFirebaseResponseErrorCode(error);

    this.notificationService.error(message + firebaseError, error, this.PAGE_NAME, true);
    this.loginPending = false;
    this.showErrors = true;
  }

  private handleCaptchaLoginError(): void {
    const message = this.translate.instant('Error logging in due to missing captcha. Please reload the page and re-try log-in ');

    this.notificationService.error(message, null, this.PAGE_NAME, true);
    this.loginPending = false;
    this.showErrors = true;
  }
}
